import React, { useState } from "react";
import { RoutingRule, Provider, Merchant } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus, Route, MoreHorizontal, Pencil, Trash2, GripVertical,
  CheckCircle2, XCircle, ArrowRight, Shuffle, GitBranch, Scale, Loader2,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const routingModeConfig = {
  priority:    { icon: GitBranch, label: "Priority",    gradient: "#22d3ee, #0284c7", accent: "#22d3ee" },
  round_robin: { icon: Shuffle,   label: "Round Robin", gradient: "#a78bfa, #7c3aed", accent: "#a78bfa" },
  weighted:    { icon: Scale,     label: "Weighted",    gradient: "#fbbf24, #d97706", accent: "#fbbf24" },
  least_load:  { icon: Route,     label: "Least Load",  gradient: "#34d399, #059669", accent: "#34d399" },
};

/* ─── Rule Card ──────────────────────────────────────────────── */
function RuleCard({ rule, merchants, onEdit, onToggle, onDelete, index }) {
  const [hovered, setHovered] = useState(false);
  const { t } = useLanguage();
  const mode = routingModeConfig[rule.routing_mode] || routingModeConfig.priority;
  const ModeIcon = mode.icon;
  const isActive = rule.status === "active";
  const providerCodes = React.useMemo(() => { try { return JSON.parse(rule.provider_sequence || "[]"); } catch { return []; } }, [rule.provider_sequence]);
  const accent = isActive ? mode.accent : "#475569";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ delay: 0.04 * index, type: "spring", stiffness: 220, damping: 24 }}
      onHoverStart={() => setHovered(true)} onHoverEnd={() => setHovered(false)}
      className="relative" style={{ perspective: 900 }}
    >
      <motion.div animate={{ opacity: hovered && isActive ? 0.8 : 0, scale: hovered ? 1.02 : 0.94 }} transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${accent}15, transparent 65%)`, filter: "blur(10px)" }}
      />
      <motion.div animate={{ y: hovered ? -5 : 0 }} transition={{ type: "spring", stiffness: 340, damping: 26 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: isActive ? "linear-gradient(135deg, rgba(15,23,42,0.88), rgba(8,14,30,0.93))" : "linear-gradient(135deg, rgba(10,16,30,0.75), rgba(5,10,22,0.8))",
          border: `1px solid ${hovered && isActive ? `${accent}35` : isActive ? "rgba(148,163,184,0.1)" : "rgba(100,116,139,0.1)"}`,
          boxShadow: hovered && isActive ? `0 18px 50px rgba(0,0,0,0.55), 0 0 30px ${accent}14` : "0 4px 16px rgba(0,0,0,0.3)",
          opacity: isActive ? 1 : 0.6,
          transition: "border-color 0.3s, box-shadow 0.3s, opacity 0.3s",
        }}
      >
        {isActive && <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />}
        {isActive && (
          <motion.div animate={{ x: hovered ? "108%" : "-108%" }} transition={{ duration: 1.2, ease: "easeInOut" }}
            className="absolute bottom-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
          />
        )}
        <div className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
          style={{ background: isActive ? `linear-gradient(180deg, ${accent}, ${accent}44)` : "#334155", transition: "background 0.3s" }}
        />
        <div className="p-5 pl-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <motion.div animate={{ opacity: hovered ? 0.7 : 0.3 }} className="mt-1 flex-shrink-0 cursor-grab">
                <GripVertical className="h-4 w-4 text-slate-500" />
              </motion.div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="font-bold text-slate-100 text-sm">{rule.name === "Default Rule" ? t("defaultRule") : rule.name}</h3>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide" style={{ background: "rgba(148,163,184,0.08)", border: "1px solid rgba(148,163,184,0.15)", color: "#64748b" }}>
                    {t("priority")} {rule.priority}
                  </span>
                  <div className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                    style={{ background: isActive ? "rgba(52,211,153,0.1)" : "rgba(100,116,139,0.08)", border: `1px solid ${isActive ? "rgba(52,211,153,0.25)" : "rgba(100,116,139,0.15)"}`, color: isActive ? "#34d399" : "#64748b" }}>
                    {isActive ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                    {isActive ? t("active") : t("inactive")}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[
                    `${t("type")}: ${rule.transaction_type || t("allTypes").toLowerCase()}`,
                    `${t("method")}: ${(rule.payment_method || t("allMethods").toLowerCase()).replace(/_/g, " ")}`,
                    `฿${rule.min_amount?.toLocaleString() || 0} – ฿${rule.max_amount?.toLocaleString() || "∞"}`,
                    ...(rule.merchant_id && rule.merchant_id !== "all" ? [`${t("merchant")}: ${merchants.find(m => m.id === rule.merchant_id)?.code || rule.merchant_id}`] : []),
                  ].map(label => (
                    <span key={label} className="text-[9px] font-semibold px-2 py-0.5 rounded-md"
                      style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", color: "#64748b" }}>
                      {label}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <motion.div whileHover={{ scale: 1.06 }}
                    className="inline-flex items-center gap-1.5 text-[9px] font-bold px-2.5 py-1 rounded-lg"
                    style={{ background: `linear-gradient(135deg, ${mode.gradient})`, boxShadow: `0 2px 10px ${mode.accent}30` }}>
                    <ModeIcon className="h-2.5 w-2.5 text-white" />
                    <span className="text-white">{mode.label}</span>
                  </motion.div>
                  {providerCodes.length > 0 && <ArrowRight className="h-3 w-3 text-slate-600" />}
                  {providerCodes.map((code, i) => (
                    <React.Fragment key={code}>
                      {i > 0 && <ArrowRight className="h-3 w-3 text-slate-600" />}
                      <motion.span whileHover={{ scale: 1.08, y: -1 }}
                        className="font-mono text-[9px] font-bold px-2 py-0.5 rounded-md"
                        style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.18)", color: "#22d3ee" }}>
                        {code}
                      </motion.span>
                    </React.Fragment>
                  ))}
                  {rule.failover_enabled && (
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-md"
                      style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.22)", color: "#fbbf24" }}>
                      {t("failover")} ({rule.max_failover_attempts}x)
                    </span>
                  )}
                </div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                  style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.12)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.06)"}
                >
                  <MoreHorizontal className="h-4 w-4 text-slate-500" />
                </motion.button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ background: "rgba(2,6,23,0.97)", border: "1px solid rgba(34,211,238,0.12)", borderRadius: 12, padding: 4, minWidth: 148, backdropFilter: "blur(16px)" }}>
                <DropdownMenuItem onClick={() => onEdit(rule)} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800 text-slate-400">
                  <Pencil className="h-3.5 w-3.5 mr-2 text-cyan-400" />{t("edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggle(rule)} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800" style={{ color: isActive ? "#f87171" : "#34d399" }}>
                  {isActive ? <XCircle className="h-3.5 w-3.5 mr-2" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-2" />}
                  {isActive ? t("disable") : t("enable")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(rule)} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800 text-red-400">
                  <Trash2 className="h-3.5 w-3.5 mr-2" />{t("delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineRouting() {
  const resellerName = "Gaming Partners Ltd";
  const engineId = "ENG001";
  const { t } = useLanguage();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const queryClient = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({ queryKey: ["routing-rules"], queryFn: () => RoutingRule.list("priority") });
  const { data: providers = [] } = useQuery({ queryKey: ["providers"], queryFn: () => Provider.list("priority") });
  const { data: merchants = [] } = useQuery({ queryKey: ["merchants"], queryFn: () => Merchant.list() });

  const createMutation = useMutation({ mutationFn: (data) => RoutingRule.create(data), onSuccess: () => { queryClient.invalidateQueries(["routing-rules"]); setFormOpen(false); } });
  const updateMutation = useMutation({ mutationFn: ({ id, data }) => RoutingRule.update(id, data), onSuccess: () => { queryClient.invalidateQueries(["routing-rules"]); setFormOpen(false); setSelectedRule(null); } });
  const deleteMutation = useMutation({ mutationFn: (id) => RoutingRule.delete(id), onSuccess: () => { queryClient.invalidateQueries(["routing-rules"]); setDeleteDialog(null); } });

  const handleSave = async (data) => {
    if (selectedRule) await updateMutation.mutateAsync({ id: selectedRule.id, data });
    else await createMutation.mutateAsync(data);
  };

  const activeRules = rules.filter(r => r.status === "active").length;

  return (
    <EngineLayout currentPage="EngineRouting" resellerName={resellerName} engineId={engineId}>
      <div className="space-y-6 w-full max-w-full">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.92))", border: "1px solid rgba(74,222,128,0.14)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(74,222,128,0.5), rgba(34,211,238,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(74,222,128,0.07), transparent 70%)" }} />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-400 mb-1">Traffic Management</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("routingRulesTitle")}</h1>
              <p className="text-sm text-slate-400">
                <span className="text-green-400 font-semibold">{activeRules}</span> / {rules.length} {t("active").toLowerCase()} {t("routingRulesTitle").toLowerCase()}
              </p>
            </div>
            <motion.button whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.96 }}
              onClick={() => { setSelectedRule(null); setFormOpen(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
              style={{ background: "linear-gradient(135deg, #4ade80, #16a34a)", boxShadow: "0 4px 20px rgba(74,222,128,0.35)", transition: "box-shadow 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 32px rgba(74,222,128,0.55)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(74,222,128,0.35)"}
            >
              <Plus className="h-4 w-4" />{t("addRule")}
            </motion.button>
          </div>
        </motion.div>

        {/* Info Banner */}
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="relative rounded-xl overflow-hidden p-4"
          style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.06), rgba(15,23,42,0.5))", border: "1px solid rgba(34,211,238,0.12)" }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent)" }} />
          <div className="flex gap-3">
            <Route className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-slate-200 mb-0.5">{t("howRoutingWorks")}</h4>
              <p className="text-xs text-slate-400">{t("routingExplanation")}</p>
            </div>
          </div>
        </motion.div>

        {/* Rules List */}
        <div className="space-y-3">
          <AnimatePresence>
            {isLoading ? (
              <motion.div key="loading" className="flex items-center justify-center py-14 gap-2">
                <Loader2 className="h-5 w-5 text-green-400 animate-spin" />
                <span className="text-sm text-slate-500">{t("loading")}</span>
              </motion.div>
            ) : rules.length === 0 ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="relative rounded-2xl overflow-hidden p-12 text-center"
                style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.7), rgba(8,14,30,0.8))", border: "1px solid rgba(148,163,184,0.08)" }}
              >
                <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.15), rgba(16,185,129,0.08))", border: "1px solid rgba(74,222,128,0.2)" }}
                >
                  <Route className="h-7 w-7 text-green-400" />
                </motion.div>
                <h3 className="text-base font-bold text-slate-200 mb-1.5">{t("noRoutingRules")}</h3>
                <p className="text-sm text-slate-500 mb-5">{t("createFirstRoutingRule")}</p>
                <motion.button whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.96 }} onClick={() => setFormOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
                  style={{ background: "linear-gradient(135deg, #4ade80, #16a34a)", boxShadow: "0 4px 18px rgba(74,222,128,0.35)" }}
                >
                  <Plus className="h-4 w-4" />{t("addRule")}
                </motion.button>
              </motion.div>
            ) : rules.map((rule, i) => (
              <RuleCard key={rule.id} rule={rule} merchants={merchants} index={i}
                onEdit={r => { setSelectedRule(r); setFormOpen(true); }}
                onToggle={r => updateMutation.mutateAsync({ id: r.id, data: { ...r, status: r.status === "active" ? "inactive" : "active" } })}
                onDelete={setDeleteDialog}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>

      <RuleFormDialog rule={selectedRule} open={formOpen} onClose={() => { setFormOpen(false); setSelectedRule(null); }} onSave={handleSave} providers={providers} merchants={merchants} />

      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(248,113,113,0.18)", borderRadius: 16 }}>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(248,113,113,0.4), transparent)" }} />
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">{t("deleteRoutingRule")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {t("deleteConfirmation")} &ldquo;{deleteDialog?.name === "Default Rule" ? t("defaultRule") : deleteDialog?.name}&rdquo;? {t("affectTransactionRouting")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.14)", color: "#94a3b8", borderRadius: 10 }}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate(deleteDialog.id)}
              style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", color: "#fff" }}>
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EngineLayout>
  );
}

/* ─── Rule Form Dialog – fully dark ─────────────────────────── */
function RuleFormDialog({ rule, open, onClose, onSave, providers, merchants }) {
  const { t } = useLanguage();
  const dflt = { name: "", status: "active", priority: 1, merchant_id: "all", transaction_type: "all", payment_method: "all", min_amount: 0, max_amount: 999999999, provider_sequence: "[]", routing_mode: "priority", failover_enabled: true, max_failover_attempts: 3, notes: "" };
  const [form, setForm] = useState(rule || dflt);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setForm(rule || dflt); }, [rule, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const providerSeq = React.useMemo(() => { try { return JSON.parse(form.provider_sequence || "[]"); } catch { return []; } }, [form.provider_sequence]);
  const toggleProvider = (code) => {
    const next = providerSeq.includes(code) ? providerSeq.filter(c => c !== code) : [...providerSeq, code];
    set("provider_sequence", JSON.stringify(next));
  };
  const handleSubmit = async () => { setSaving(true); await onSave(form); setSaving(false); };

  /* unified dark input style with cyan focus */
  const inp = (extra = {}) => ({
    width: "100%", padding: "9px 12px", borderRadius: 10, fontSize: 13,
    background: "rgba(2,6,23,0.7)", border: "1px solid rgba(148,163,184,0.1)",
    color: "#e2e8f0", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s",
    ...extra,
  });
  const focus = e => { e.target.style.borderColor = "rgba(74,222,128,0.3)"; e.target.style.boxShadow = "0 0 0 3px rgba(74,222,128,0.06)"; };
  const blur  = e => { e.target.style.borderColor = "rgba(148,163,184,0.1)"; e.target.style.boxShadow = "none"; };

  const FL = ({ children }) => <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{children}</label>;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(74,222,128,0.14)", borderRadius: 20 }}>
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(74,222,128,0.45), rgba(34,211,238,0.25), transparent)" }} />
        <DialogHeader>
          <DialogTitle className="text-slate-100">{rule ? t("editRoutingRule") : t("createRoutingRule")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-3">
          {/* Name + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div><FL>{t("ruleName")} *</FL><input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g., Default QR Route" style={inp()} onFocus={focus} onBlur={blur} /></div>
            <div><FL>{t("priority")}</FL><input type="number" value={form.priority} onChange={e => set("priority", parseInt(e.target.value))} min={1} style={inp()} onFocus={focus} onBlur={blur} /></div>
          </div>

          {/* Type + Method */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: t("transactionType"), key: "transaction_type", options: [{ v: "all", l: t("allTypes") }, { v: "deposit", l: t("depositsOnly") }, { v: "withdrawal", l: t("withdrawalsOnly") }] },
              { label: t("paymentMethod"),   key: "payment_method",   options: [{ v: "all", l: t("allMethods") }, { v: "promptpay_qr", l: "PromptPay QR" }, { v: "bank_transfer", l: "Bank Transfer" }, { v: "truemoney", l: "TrueMoney" }] },
            ].map(f => (
              <div key={f.key}>
                <FL>{f.label}</FL>
                <select value={form[f.key]} onChange={e => set(f.key, e.target.value)} style={{ ...inp(), cursor: "pointer" }} onFocus={focus} onBlur={blur}>
                  {f.options.map(o => <option key={o.v} value={o.v} style={{ background: "#020617" }}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>

          {/* Amount range */}
          <div className="grid grid-cols-2 gap-3">
            <div><FL>{t("minAmount")}</FL><input type="number" value={form.min_amount} onChange={e => set("min_amount", parseInt(e.target.value))} style={inp()} onFocus={focus} onBlur={blur} /></div>
            <div><FL>{t("maxAmount")}</FL><input type="number" value={form.max_amount} onChange={e => set("max_amount", parseInt(e.target.value))} style={inp()} onFocus={focus} onBlur={blur} /></div>
          </div>

          {/* Merchant */}
          <div>
            <FL>{t("specificMerchant")}</FL>
            <select value={form.merchant_id || "all"} onChange={e => set("merchant_id", e.target.value)} style={{ ...inp(), cursor: "pointer" }} onFocus={focus} onBlur={blur}>
              <option value="all" style={{ background: "#020617" }}>{t("allMerchants")}</option>
              {merchants.map(m => <option key={m.id} value={m.id} style={{ background: "#020617" }}>{m.name} ({m.code})</option>)}
            </select>
          </div>

          {/* Routing mode */}
          <div>
            <FL>{t("routingMode")}</FL>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(routingModeConfig).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const selected = form.routing_mode === key;
                return (
                  <motion.button key={key} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    onClick={() => set("routing_mode", key)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl cursor-pointer outline-none border-0"
                    style={{
                      background: selected ? `linear-gradient(135deg, ${cfg.gradient})` : "rgba(2,6,23,0.7)",
                      border: `1px solid ${selected ? `${cfg.accent}50` : "rgba(148,163,184,0.1)"}`,
                      boxShadow: selected ? `0 4px 16px ${cfg.accent}30` : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    <Icon className="h-4 w-4" style={{ color: selected ? "#fff" : "#64748b" }} />
                    <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: selected ? "#fff" : "#64748b" }}>{cfg.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Provider sequence */}
          <div>
            <FL>{t("providerSequence")}</FL>
            <div className="flex flex-wrap gap-2 p-4 rounded-xl min-h-[56px]" style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.08)" }}>
              {providers.map(p => {
                const sel = providerSeq.includes(p.code);
                return (
                  <motion.button key={p.id} whileHover={{ scale: 1.08, y: -1 }} whileTap={{ scale: 0.95 }}
                    onClick={() => toggleProvider(p.code)}
                    className="font-mono text-[10px] font-bold px-2.5 py-1 rounded-lg cursor-pointer outline-none border-0"
                    style={{
                      background: sel ? "linear-gradient(135deg, #22d3ee, #0284c7)" : "rgba(34,211,238,0.06)",
                      border: `1px solid ${sel ? "rgba(34,211,238,0.4)" : "rgba(34,211,238,0.14)"}`,
                      color: sel ? "#fff" : "#64748b",
                      boxShadow: sel ? "0 2px 10px rgba(34,211,238,0.25)" : "none",
                      transition: "all 0.18s",
                    }}
                  >
                    {p.code}{sel && <span className="ml-1 opacity-75">#{providerSeq.indexOf(p.code) + 1}</span>}
                  </motion.button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-600 mt-1.5">{t("clickProvidersOrder")}</p>
          </div>

          {/* Failover toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.08)" }}>
            <div>
              <p className="text-sm font-semibold text-slate-300">{t("enableFailover")}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t("autoTryNextProvider")}</p>
            </div>
            <motion.div onClick={() => set("failover_enabled", !form.failover_enabled)} whileTap={{ scale: 0.95 }}
              style={{
                width: 42, height: 23, borderRadius: 12,
                background: form.failover_enabled ? "linear-gradient(135deg, #4ade80, #16a34a)" : "rgba(34,211,238,0.08)",
                border: `1px solid ${form.failover_enabled ? "rgba(74,222,128,0.4)" : "rgba(34,211,238,0.14)"}`,
                cursor: "pointer", position: "relative", transition: "all 0.3s",
              }}
            >
              <motion.div animate={{ left: form.failover_enabled ? 20 : 2 }} transition={{ type: "spring", stiffness: 380, damping: 24 }}
                style={{ position: "absolute", top: 2, width: 17, height: 17, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }}
              />
            </motion.div>
          </div>

          {form.failover_enabled && (
            <div>
              <FL>{t("maxFailoverAttempts")}</FL>
              <input type="number" value={form.max_failover_attempts} onChange={e => set("max_failover_attempts", parseInt(e.target.value))} min={1} max={10} style={inp()} onFocus={focus} onBlur={blur} />
            </div>
          )}
        </div>

        <DialogFooter className="mt-5 gap-2">
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-400 cursor-pointer outline-none border-0"
            style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)" }}
          >
            {t("cancel")}
          </motion.button>
          <motion.button whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }} onClick={handleSubmit}
            disabled={saving || !form.name}
            className="px-5 py-2 rounded-xl text-sm font-bold text-white cursor-pointer outline-none border-0"
            style={{ background: "linear-gradient(135deg, #4ade80, #16a34a)", boxShadow: "0 4px 16px rgba(74,222,128,0.35)", opacity: (saving || !form.name) ? 0.55 : 1, transition: "opacity 0.2s" }}
          >
            {saving ? t("saving") : rule ? t("update") : t("create")}
          </motion.button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}